import { callIRISRich, getIRISErrorMessage, IRISError } from '../services/IRIS';
import { AGENT_IDS } from '../constants/fillyConstants';
import { AsanaTask } from '../services/asanaService';

// Interface for the JSON format required by IRIS narrative agent
export interface NarrativeTaskData {
  title: string;
  program: string;
  problem_statement: string;
  desired_outcome: string;
  impact_metrics: string;
  annualized_entitlement: string;
  sde_headcount: string;
  target_year: string;
  total_org_sde_headcount: string;
  prioritization_rationale: string;
  organization_charter: string;
}

// Interface for organizational context data
export interface OrganizationContext {
  name?: string;
  missionGoals?: Array<{
    title: string;
    description: string;
    status: string;
    tags: string[];
  }>;
  keyPriorities?: string[];
  tenets?: string[];
  charter?: string;
}

// Interface for L7 manager data
export interface L7ManagerData {
  totalTargetSDE: number;
  totalActualSDE: number;
  totalTargetHC: number;
  totalActualHC: number;
  totalRTB: number;
}

// Interface for utility options
export interface NarrativeUtilityOptions {
  tasks: AsanaTask[];
  organizationContext?: OrganizationContext;
  l7Data?: L7ManagerData;
  organizationName?: string;
}

// Interface for processed narrative response
export interface NarrativeResponse {
  success: boolean;
  rawResponse: string;
  processedContent?: {
    summary?: string;
    keyInsights?: string[];
    recommendations?: string[];
    strategicNarrative?: string;
  };
  error?: string;
  metadata?: {
    processingTime: number;
    taskCount: number;
    totalEntitlement: number;
    totalSdeHeadcount: number;
  };
}

/**
 * Transforms selected tasks into the narrative JSON format required by IRIS
 */
export const transformTasksToNarrativeFormat = (
  tasks: AsanaTask[],
  organizationContext?: OrganizationContext,
  l7Data?: L7ManagerData
): NarrativeTaskData[] => {
  return tasks.map(task => {
    // Calculate organization charter from context
    let organizationCharter = '';
    if (organizationContext) {
      const parts: string[] = [];
      
      if (organizationContext.charter) {
        parts.push(organizationContext.charter);
      }
      
      if (organizationContext.missionGoals && organizationContext.missionGoals.length > 0) {
        const activeMissionGoals = organizationContext.missionGoals
          .filter(goal => goal.status === 'active')
          .map(goal => `${goal.title}: ${goal.description}`)
          .join('; ');
        if (activeMissionGoals) {
          parts.push(`Mission Goals: ${activeMissionGoals}`);
        }
      }
      
      if (organizationContext.keyPriorities && organizationContext.keyPriorities.length > 0) {
        parts.push(`Key Priorities: ${organizationContext.keyPriorities.join(', ')}`);
      }
      
      if (organizationContext.tenets && organizationContext.tenets.length > 0) {
        parts.push(`Organizational Tenets: ${organizationContext.tenets.join(', ')}`);
      }
      
      organizationCharter = parts.join(' | ');
    }

    return {
      title: task.name || 'Untitled Task',
      program: task.program || 'Not specified',
      problem_statement: task.problem_statement || 'Not specified',
      desired_outcome: task.desired_outcome || 'Not specified',
      impact_metrics: (task as any).impact_metrics || 'Not specified',
      annualized_entitlement: (task as any).annualized_entitlement?.toString() || '0',
      sde_headcount: (task as any).hc_sde?.toString() || '0',
      target_year: (task as any).target_year || new Date().getFullYear().toString(),
      total_org_sde_headcount: l7Data?.totalTargetSDE?.toString() || '0',
      prioritization_rationale: (task as any).rationale || (task as any).op_decision || 'Not specified',
      organization_charter: organizationCharter || 'Not specified'
    };
  });
};

/**
 * Validates that narrative data has all required fields
 */
export const validateNarrativeData = (data: NarrativeTaskData[]): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!data || data.length === 0) {
    errors.push('No tasks provided for narrative generation');
    return { isValid: false, errors };
  }

  data.forEach((task, index) => {
    const requiredFields: (keyof NarrativeTaskData)[] = [
      'title', 'program', 
    //   'problem_statement', 'desired_outcome', 
    //   'impact_metrics', 'annualized_entitlement', 'sde_headcount', 'target_year'
    ];

    requiredFields.forEach(field => {
      if (!task[field] || task[field].trim() === '' || task[field] === 'Not specified') {
        errors.push(`Task ${index + 1}: Missing or empty ${field}`);
      }
    });
  });

  return { isValid: errors.length === 0, errors };
};

/**
 * Sends formatted task data to IRIS narrative agent
 */
export const sendTasksToNarrativeAgent = async (
  narrativeData: NarrativeTaskData[],
  organizationName?: string
): Promise<string> => {
  const startTime = Date.now();
  
  try {
    // Validate data before sending
    const validation = validateNarrativeData(narrativeData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    // Create the prompt for the narrative agent
    const prompt = `Please create a strategic narrative for the following ${narrativeData.length} prioritized initiative${narrativeData.length > 1 ? 's' : ''}${organizationName ? ` for ${organizationName}` : ''}. 

You are a highly skilled Principal Product Manager specializing in crafting Amazon-style narrative documentation with exceptional clarity, precision, and strategic insight. Your goal is to transform feature requests into single compelling, structured strategic narrative of no more than 500 words. Adhere to stricty format, No other commentry.

#Program Name# (Entitlement: $xx, SDE HC: x)
#Narrative#


TASK DATA:
${JSON.stringify(narrativeData, null, 2)}

 `;

    console.log('🔮 NARRATIVE: Sending tasks to IRIS narrative agent...', {
      taskCount: narrativeData.length,
      agentId: AGENT_IDS.narrative,narrativeData
    });

    const response = await callIRISRich({
      userMessage: prompt,
      stage: 'finaljson',
      agentId: AGENT_IDS.narrative
    });

    const processingTime = Date.now() - startTime;
    console.log(`✅ NARRATIVE: Received response in ${processingTime}ms`);

    return response;
  } catch (error) {
    console.error('❌ NARRATIVE: Failed to send tasks to narrative agent:', error);
    
    // Use the generic IRIS error message generator
    const userFriendlyMessage = getIRISErrorMessage(error, 'Narrative generation');
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Parses and processes the narrative response from Hey Filly
 * Handles IRIS response format: # Program Name (Entitlement: $xx, SDE HC: x) ## Narrative
 */
export const parseNarrativeResponse = (rawResponse: string): NarrativeResponse['processedContent'] => {
  try {
    let content = rawResponse;
    
    // Clean up common formatting issues
    if (content.includes('```')) {
      // Remove code blocks
      content = content.replace(/```[\s\S]*?```/g, '').trim();
    }
    
    // For IRIS format, just return the content as strategic narrative
    // The format will be: # Program Name (Entitlement: $xx, SDE HC: x) ## Narrative
    return {
      strategicNarrative: content
    };
  } catch (error) {
    console.warn('⚠️ NARRATIVE: Failed to parse response, using raw content:', error);
    return {
      strategicNarrative: rawResponse
    };
  }
};

/**
 * Complete workflow: Transform tasks, send to IRIS, and process response
 */
export const generateNarrativeForTasks = async (
  options: NarrativeUtilityOptions
): Promise<NarrativeResponse> => {
  const startTime = Date.now();
  
  try {
    console.log('🚀 NARRATIVE: Starting narrative generation workflow...', {
      taskCount: options.tasks.length,
      organizationName: options.organizationName
    });

    // Step 1: Transform tasks to narrative format
    const narrativeData = transformTasksToNarrativeFormat(
      options.tasks,
      options.organizationContext,
      options.l7Data
    );

    // Step 2: Send to IRIS narrative agent
    const rawResponse = await sendTasksToNarrativeAgent(
      narrativeData,
      options.organizationName
    );

    // Step 3: Process the response
    const processedContent = parseNarrativeResponse(rawResponse);

    // Step 4: Calculate metadata
    const totalEntitlement = options.tasks.reduce((sum, task) => {
      const entitlement = parseFloat((task as any).annualized_entitlement) || 0;
      return sum + entitlement;
    }, 0);

    const totalSdeHeadcount = options.tasks.reduce((sum, task) => {
      const hc = parseFloat((task as any).hc_sde) || 0;
      return sum + hc;
    }, 0);

    const processingTime = Date.now() - startTime;

    const response: NarrativeResponse = {
      success: true,
      rawResponse,
      processedContent,
      metadata: {
        processingTime,
        taskCount: options.tasks.length,
        totalEntitlement,
        totalSdeHeadcount
      }
    };

    console.log('✅ NARRATIVE: Workflow completed successfully', {
      processingTime,
      taskCount: options.tasks.length,
      hasProcessedContent: !!processedContent
    });

    return response;
  } catch (error) {
    const processingTime = Date.now() - startTime;
    console.error('❌ NARRATIVE: Workflow failed:', error);

    return {
      success: false,
      rawResponse: '',
      error: error instanceof Error ? error.message : 'Unknown error occurred',
      metadata: {
        processingTime,
        taskCount: options.tasks.length,
        totalEntitlement: 0,
        totalSdeHeadcount: 0
      }
    };
  }
};

/**
 * Utility function to format narrative response for display
 */
export const formatNarrativeForDisplay = (response: NarrativeResponse): string => {
  if (!response.success) {
    return `Error: ${response.error}`;
  }

  const content = response.processedContent;
  if (!content || !content.strategicNarrative) {
    return response.rawResponse;
  }

  // For IRIS format, just return the strategic narrative as-is
  // It will already be formatted as: # Program Name (Entitlement: $xx, SDE HC: x) ## Narrative
  return content.strategicNarrative;
};

/**
 * Export utility functions for external use
 */
export const narrativeUtility = {
  transformTasksToNarrativeFormat,
  validateNarrativeData,
  sendTasksToNarrativeAgent,
  parseNarrativeResponse,
  generateNarrativeForTasks,
  formatNarrativeForDisplay
};

export default narrativeUtility;
